import { createStore } from 'zustand/vanilla';
import { devtools } from 'zustand/middleware';
import type { WebSocketMessage } from '../types';
import { moFamilyStore } from './moFamilyStore';

interface WebSocketState {
    ws: WebSocket | null;
    isConnected: boolean;
    reconnectAttempts: number;
    messageHandlers: Map<string, (message: WebSocketMessage) => void>;
    
    // Actions
    connect: (apiBase: string) => void;
    disconnect: () => void;
    send: (message: any) => void;
    registerHandler: (type: string, handler: (message: WebSocketMessage) => void) => void;
    unregisterHandler: (type: string) => void;
    subscribeToFamily: (familyHash: string) => void;
    unsubscribeFromFamily: (familyHash: string) => void;
}

export const webSocketStore = createStore<WebSocketState>()(
    devtools((set, get) => ({
        ws: null,
        isConnected: false,
        reconnectAttempts: 0,
        messageHandlers: new Map(),
        
        connect: (apiBase) => {
            const state = get();
            
            // Don't reconnect if already connected
            if (state.ws && state.ws.readyState === WebSocket.OPEN) {
                return;
            }
            
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsHost = apiBase.replace(/^https?:/, wsProtocol);
            
            try {
                const ws = new WebSocket(`${wsHost}/ws`);
                
                ws.onopen = () => {
                    set({ ws, isConnected: true, reconnectAttempts: 0 });
                };
                
                ws.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);
                        
                        // Handle message with registered handlers
                        const handlers = get().messageHandlers;
                        const handler = handlers.get(message.type);
                        if (handler) {
                            handler(message);
                        }
                        
                        // Always pass to MoFamily store
                        if ('id' in message && 'timestamp' in message) {
                            moFamilyStore.getState().handleWebSocketMessage(message);
                        }
                    } catch (error) {
                    }
                };
                
                ws.onclose = (event) => {
                    set({ ws: null, isConnected: false });
                    
                    // Auto-reconnect for non-normal closures
                    if (event.code !== 1000 && event.code !== 1001 && get().reconnectAttempts < 5) {
                        const attempts = get().reconnectAttempts;
                        set({ reconnectAttempts: attempts + 1 });
                        setTimeout(() => get().connect(apiBase), 5000);
                    }
                };
                
                ws.onerror = (error) => {
                };
                
                set({ ws });
            } catch (error) {
                console.error('[WebSocketStore] Connection failed:', error);
            }
        },
        
        disconnect: () => {
            const { ws } = get();
            if (ws) {
                ws.close(1000, 'Normal closure');
                set({ ws: null, isConnected: false });
            }
        },
        
        send: (message) => {
            const { ws, isConnected } = get();
            if (ws && isConnected && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify(message));
            }
        },
        
        registerHandler: (type, handler) => {
            set((state) => {
                const handlers = new Map(state.messageHandlers);
                handlers.set(type, handler);
                return { messageHandlers: handlers };
            });
        },
        
        unregisterHandler: (type) => {
            set((state) => {
                const handlers = new Map(state.messageHandlers);
                handlers.delete(type);
                return { messageHandlers: handlers };
            });
        },
        
        subscribeToFamily: (familyHash) => {
            get().send({
                type: 'subscribe_family',
                familyHash
            });
        },
        
        unsubscribeFromFamily: (familyHash) => {
            get().send({
                type: 'unsubscribe_family',
                familyHash
            });
        }
    }))
);